import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { IMG } from "@/components/data";
import { useLang } from "@/i18n";
import { ArrowDownRight } from "lucide-react";

const lineV = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "-25%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  const go = () => document.querySelector("#preventivo")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="top" ref={ref} className="relative pt-[72px] overflow-hidden bg-white">
      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 pt-[26px] md:pt-[50px] pb-10">
          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-3 mb-8"
            >
              <span className="w-8 h-[2px] bg-ferrari" />
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-ink/60">{t.hero.label}</span>
            </motion.div>

            <h1 className="font-display font-extrabold uppercase leading-[0.9] tracking-tighter text-[10vw] sm:text-[8.5vw] md:text-[8vw] lg:text-[6vw] max-w-full break-words">
              {t.hero.lines.map((l, i) => (
                <span key={l} className="mask-line lg:whitespace-nowrap">
                  <motion.span
                    className={`block ${i === 1 ? "text-ferrari" : "text-ink"}`}
                    custom={i}
                    variants={lineV}
                    initial="hidden"
                    animate="show"
                    data-testid={`hero-line-${i}`}
                  >
                    {l}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.7 }}
              className="mt-9 flex flex-col md:flex-row md:items-end gap-8 max-w-3xl"
            >
              <p className="text-base md:text-lg text-ink/70 leading-relaxed max-w-md">{t.hero.subtitle}</p>
              <button
                data-testid="hero-quote-btn"
                onClick={go}
                className="group inline-flex items-center gap-3 bg-ferrari text-white font-bold uppercase tracking-[0.12em] text-sm px-8 py-4 hover:bg-ink transition-colors duration-200 whitespace-nowrap"
              >
                {t.hero.cta}
                <ArrowDownRight size={18} className="group-hover:rotate-45 transition-transform duration-300" />
              </button>
            </motion.div>
          </div>

          <div className="lg:col-span-4 relative">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="relative h-[300px] lg:h-full min-h-[340px] overflow-hidden rounded-2xl"
            >
              <motion.img
                src={IMG.pellets}
                alt="Pellet di legno"
                style={{ y, scale }}
                className="absolute inset-0 w-full h-[130%] object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-ferrari text-white grain px-5 py-4 flex items-center justify-between">
                <span className="font-display font-bold uppercase tracking-tight text-lg">{t.hero.badge}</span>
                <span className="text-xs tracking-[0.2em] uppercase">EN plus A1</span>
              </div>
            </motion.div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 border-t md:border-b border-ink/80">
          {t.hero.stats.map(([k, v], i) => (
            <motion.div
              key={k + i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.5 }}
              className={`py-7 md:py-9 px-2 md:px-5 ${i !== 3 ? "md:border-r" : ""} border-b md:border-b-0 border-ink/80 ${i % 2 === 0 ? "border-r md:border-r" : ""}`}
            >
              <div className="font-display font-extrabold text-3xl md:text-4xl tracking-tighter">{k}</div>
              <div className="text-xs uppercase tracking-[0.15em] text-ink/60 mt-2">{v}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
